import React from 'react';
import Table, { TableProps } from '@cloudscape-design/components/table';
import Box from '@cloudscape-design/components/box';
import { ModelMetricsAggregated, labelModel } from '../../types';

interface RatingHeatmapProps {
  ratings: Record<string, Record<string, number>>;
  models: Record<string, ModelMetricsAggregated>;
}

function getCellColor(value: number): string {
  // Map 0–10 to a red-to-green gradient
  const clamped = Math.max(0, Math.min(10, value));
  const ratio = clamped / 10;
  // Red (low) -> Yellow (mid) -> Green (high)
  const r = Math.round(255 * (1 - ratio));
  const g = Math.round(255 * ratio);
  return `rgba(${r}, ${g}, 60, 0.25)`;
}

function getModelDisplayName(
  modelId: string,
  models: Record<string, ModelMetricsAggregated>
): string {
  const m = models[modelId];
  return m ? labelModel(m) : modelId;
}

export const RatingHeatmap: React.FC<RatingHeatmapProps> = ({ ratings, models }) => {
  const raterIds = Object.keys(ratings);

  if (raterIds.length === 0) {
    return (
      <Box textAlign="center" color="text-status-inactive" padding="l">
        No rating data available
      </Box>
    );
  }

  // Collect all rated model IDs across all raters
  const ratedIdSet = new Set<string>();
  for (const rater of raterIds) {
    for (const rated of Object.keys(ratings[rater])) {
      ratedIdSet.add(rated);
    }
  }
  const ratedIds = Array.from(ratedIdSet).sort();

  // Build table rows: one per rater
  interface HeatmapRow {
    rater: string;
    [ratedModelId: string]: string | number;
  }

  const items: HeatmapRow[] = raterIds.sort().map((raterId) => {
    const row: HeatmapRow = { rater: getModelDisplayName(raterId, models) };
    for (const ratedId of ratedIds) {
      row[ratedId] = ratings[raterId]?.[ratedId] ?? '-';
    }
    return row;
  });

  const columnDefinitions: TableProps.ColumnDefinition<HeatmapRow>[] = [
    {
      id: 'rater',
      header: 'Rater \\ Rated',
      cell: (item) => <Box fontWeight="bold">{item.rater}</Box>,
      width: 160,
    },
    ...ratedIds.map((ratedId) => ({
      id: ratedId,
      header: getModelDisplayName(ratedId, models),
      cell: (item: HeatmapRow) => {
        const value = item[ratedId];
        if (value === '-' || value === undefined) {
          return <Box textAlign="center">-</Box>;
        }
        const numValue = Number(value);
        const rounded = Math.round(numValue * 10) / 10;
        return (
          <Box textAlign="center">
            <span
              style={{
                display: 'inline-block',
                width: '100%',
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: getCellColor(numValue),
                fontWeight: 'bold',
              }}
            >
              {rounded}
            </span>
          </Box>
        );
      },
      width: 120,
    })),
  ];

  return (
    <Table
      columnDefinitions={columnDefinitions}
      items={items}
      variant="embedded"
      wrapLines
    />
  );
};
